import React, { useState, useEffect } from 'react';
import { Sparkles, Save, Loader, AlertTriangle, CheckCircle, Edit3, X, Check } from 'lucide-react';
import { getAllPrompts, updatePrompt } from '../../services/settingsapi';
import ConfirmationDialog from '../ConfirmationDialog';
import { SettingsSection } from '../SettingsUIComponents';

const AIPromptsSettings = ({ canEditSettings, canViewSettings }) => {
  const [prompts, setPrompts] = useState({});
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsMessage, setPromptsMessage] = useState({ type: '', text: '' });
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editingPromptText, setEditingPromptText] = useState('');

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Fetch all prompts on component mount
  useEffect(() => {
    if (!canViewSettings) return;

    const fetchAllPrompts = async () => {
      try {
        setPromptsLoading(true);
        const data = await getAllPrompts();
        setPrompts(data);
        setPromptsMessage({ type: '', text: '' });
      } catch (error) {
        console.error('Error fetching prompts:', error);
        setPromptsMessage({
          type: 'error',
          text: 'Error loading prompts: ' + error.message
        });
      } finally {
        setPromptsLoading(false);
      }
    };

    fetchAllPrompts();
  }, [canViewSettings]);

  // Save single prompt
  const savePrompt = async (promptName) => {
    if (!canEditSettings) {
      setPromptsMessage({
        type: 'error',
        text: 'You do not have permission to edit settings'
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: 'Confirm Prompt Update',
      message: `Are you sure you want to update the "${promptName}" prompt? This will affect all AI interactions using this prompt.`,
      onConfirm: async () => {
        try {
          setPromptsLoading(true);
          await updatePrompt(promptName, editingPromptText);
          setPrompts(prev => ({
            ...prev,
            [promptName]: editingPromptText
          }));
          setEditingPrompt(null);
          setEditingPromptText('');
          setPromptsMessage({
            type: 'success',
            text: `Prompt "${promptName}" updated successfully!`
          });

          setTimeout(() => setPromptsMessage({ type: '', text: '' }), 5000);
        } catch (error) {
          console.error(`Error saving prompt ${promptName}:`, error);
          setPromptsMessage({
            type: 'error',
            text: `Error saving prompt: ${error.message}`
          });
        } finally {
          setPromptsLoading(false);
        }
      }
    });
  };

  return (
    <>
      <SettingsSection
        info="Customize AI prompts to fine-tune the behavior and responses of AI models. Changes apply to all new interviews immediately."
        icon={Sparkles}
        title="AI Prompts Configuration"
        description="Manage and customize AI prompts for different interview scenarios"
      >
        <div className="space-y-6">
          {promptsMessage.text && (
            <div className={`p-4 border rounded-xl flex items-center gap-3 ${
              promptsMessage.type === 'success'
                ? 'bg-green-50 border-green-300'
                : 'bg-red-50 border-red-300'
            }`}>
              {promptsMessage.type === 'success' ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p className={`text-sm font-medium ${promptsMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {promptsMessage.text}
              </p>
            </div>
          )}

          {promptsLoading && !Object.keys(prompts).length ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-teal-600" />
            </div>
          ) : (
            <div className="settings-prompts-grid">
              {!Object.keys(prompts).length && <p className="settings-empty-state">No AI prompts are available.</p>}
              {Object.entries(prompts).map(([promptName, promptText]) => (
                <div
                  key={promptName}
                  className="settings-prompt-card bg-[var(--bg-card)] rounded-2xl shadow-md border border-[var(--border-color)] overflow-hidden hover:shadow-xl hover:border-[var(--primary)] transition-all duration-300 flex flex-col"
                >
                  {/* Card Header */}
                  <div className="bg-[var(--primary-light)] px-6 py-5 border-b border-[var(--border-color)]">
                    <div className="flex items-start gap-3">
                      <div className="bg-[var(--bg-card)] p-2.5 rounded-lg flex-shrink-0 shadow-sm">
                        <Sparkles className="w-5 h-5 text-[var(--primary)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[var(--text-primary)] font-bold text-base leading-tight" title={promptName}>
                          {promptName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h3>
                        <p className="text-xs text-[var(--text-muted)] mt-1">AI Prompt Configuration</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="flex-1 p-6">
                    {editingPrompt === promptName ? (
                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-[var(--text-secondary)]">Prompt Content</label>
                        <textarea
                          value={editingPromptText}
                          onChange={(e) => setEditingPromptText(e.target.value)}
                          className="w-full h-48 p-4 border border-[var(--border-color)] rounded-xl text-sm font-mono resize-none focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary-light)] bg-[var(--bg-input)] text-[var(--text-primary)]"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">Prompt Content</label>
                        <div className="h-48 p-4 bg-[var(--bg-input)] rounded-xl border border-[var(--border-color)] overflow-y-auto">
                          <p className="text-[var(--text-primary)] text-sm leading-relaxed whitespace-pre-wrap break-words font-mono">
                            {promptText}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div className="bg-[var(--bg-card)] px-6 py-4 border-t border-[var(--border-color)] flex gap-2">
                    {editingPrompt === promptName ? (
                      <>
                        <button
                          onClick={() => savePrompt(promptName)}
                          disabled={promptsLoading || !canEditSettings}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                            canEditSettings && !promptsLoading
                              ? 'bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-md'
                              : 'bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                          }`}
                        >
                          {promptsLoading ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingPrompt(null);
                            setEditingPromptText('');
                          }}
                          disabled={promptsLoading}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--primary-light)] transition-all duration-200"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingPrompt(promptName);
                          setEditingPromptText(promptText);
                        }}
                        disabled={!canEditSettings}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                          canEditSettings
                            ? 'bg-[var(--bg-card)] border-2 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white'
                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        }`}
                      >
                        <Edit3 className="w-4 h-4" />
                        Edit Prompt
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="settings-info-banner settings-prompts-info">
            <p className="text-sm text-teal-800">
              <span className="font-semibold">Info:</span> Customize AI prompts to fine-tune the behavior and responses of AI models across different interview scenarios. Changes will apply to all new interviews immediately.
            </p>
          </div>
        </div>
      </SettingsSection>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Update Prompt"
        cancelText="Cancel"
        isDangerous={false}
      />
    </>
  );
};

export default AIPromptsSettings;
